<template>
    <section class="my-account flow__panel">
        <div class="my-account__main">
            <h2 class="my-account__title">
                {{ myResources.title }}
            </h2>
            <hh-msg-display :message="message"
                            :type="msgType" />
            <hh-error-display :error="error" />
            <dl v-if="profile"
                class="my-account__data">
                <div v-if="profile.hasName"
                     class="my-account__data__item">
                    <dt class="my-account__data__item__label">
                        {{ myResources.lblName }}
                    </dt>
                    <dd class="my-account__data__item__value">
                        {{ profile.fullName }}
                    </dd>
                </div>
                <div v-if="profile.hasEmail"
                     class="my-account__data__item">
                    <dt class="my-account__data__item__label">
                        {{ myResources.lblEmail }}
                    </dt>
                    <dd class="my-account__data__item__value">
                        {{ profile.email }}
                    </dd>
                </div>
                <div v-if="profile.phoneNumber"
                     class="my-account__data__item">
                    <dt class="my-account__data__item__label">
                        {{ myResources.lblPhoneNumber }}
                    </dt>
                    <dd class="my-account__data__item__value">
                        {{ profile.phoneNumber }}
                    </dd>
                </div>
                <consent-manager v-if="supportsMarketingConsent"
                                 class="my-account__data__item my-account__data__marketing-consent"
                                 :label="myResources.lblMarketingConsent"
                                 :display="profile.marketingConsent"
                                 :granted="profile.marketingConsentGranted"
                                 @withdraw="onWithdrawConsent('marketing')"
                                 @grant="onGrantConsent('marketing')" />
                <consent-manager v-if="supportsEvaluationConsent"
                                 class="my-account__data__item my-account__data__evaluation-consent"
                                 :label="myResources.lblEvaluatorContactConsent"
                                 :display="profile.evaluationConsent"
                                 :granted="profile.evaluationConsentGranted"
                                 @withdraw="onWithdrawConsent('evaluation')"
                                 @grant="onGrantConsent('evaluation')" />
                <div class="my-account__data__text">
                    <p>{{ myResources.updateText }}</p>
                </div>
                <consent-manager v-if="cookieConsent"
                                 class="my-account__data__item my-account__data__analytics-consent"
                                 :label="myResources.lblAnalysisConsent"
                                 :display="displayAcceptedOrWithdrawn(cookieConsent.analytics)"
                                 :granted="cookieConsent.analytics"
                                 @withdraw="onWithdrawCookieConsent('analytics')"
                                 @grant="onGrantCookieConsent('analytics')" />
                <consent-manager v-if="cookieConsent"
                                 class="my-account__data__item my-account__data__app-consent"
                                 :label="myResources.lblAppConsent"
                                 :display="displayAcceptedOrWithdrawn(cookieConsent.appCore)"
                                 :granted="cookieConsent.appCore"
                                 @withdraw="onWithdrawCookieConsent('appCore')"
                                 @grant="onGrantCookieConsent('appCore')" />
                <div v-if="profile.hasContactStatus"
                     class="my-account__data__item">
                    <dt class="my-account__data__item__label">
                        {{ myResources.lblContactStatus }}
                    </dt>
                    <dd class="my-account__data__item__value">
                        {{ profile.contactStatus }}
                    </dd>
                </div>
            </dl>
            <loading-animation v-else class="my-account__loading" />
            <div class="my-account__operations">
                <password-changer class="my-account__operations__password"
                                  :show-start-link-desktop="false"
                                  :show="showPasswordChanger"
                                  @cancel="onPasswordChangeCanceled"
                                  @changed="onPasswordChanged"
                                  @clear-validation="onClearValidation" />
            </div>
        </div>
        <div class="my-account__buttons">
            <hh-btn ref="logOutButton"
                    class="my-account__buttons__button my-account__buttons__button--log-out"
                    icon-class="fas fa-sign-out-alt"
                    icon-location="pre"
                    :text="commonResources.logout"
                    @click="onLogOutClick" />
            <hh-btn class="my-account__buttons__button my-account__buttons__button--change-password"
                    icon-class="fas fa-key"
                    icon-location="pre"
                    :text="myResources.btnChangePassword"
                    @click="onChangePasswordClick" />
            <hh-btn ref="deleteButton"
                    class="my-account__buttons__button my-account__buttons__button--delete"
                    type="cta-danger"
                    icon-class="far fa-trash-alt"
                    icon-location="pre"
                    :text="myResources.btnDeleteAccount"
                    @click="onDeleteClick" />
            <hh-btn v-if="profile && profile.isInOngoingContact"
                    class="my-account__buttons__button my-account__buttons__button--contact"
                    icon-class="fas fa-check"
                    icon-location="pre"
                    :text="myResources.btnConfirmContact"
                    @click="onContactMadeClick" />
        </div>
    </section>
</template>

<script>

import Vuex from 'vuex'

import CookieConsentManager from '@/cookie-consent-manager'
import UserManager from '@/user-manager'

import AnalyticsPage from './analytics-page'

import ConsentManager from '@/components/my-account/consent-manager'
import DeleteConfirmation from '@/components/my-account/modals/account-delete-confirmation'
import LogOutConfirmation from '@/components/auth/modals/log-out-confirmation'
import PasswordChanger from '@/components/my-account/password-changer'
import PropertyManager from '@/property-manager'
import LoadingAnimation from '@/components/app-core/loading-animation'
import InstanceTypeHelper from '@/components/instance-type-helper'
import ResourcesHelper from '@/components/resources-helper'


/**
 * Default ame to use for logging.
 * @type {string}
 */
const DEFAULT_LOG_NAME = 'MyAccount'

/**
 * Name to use for logging modal callbacks.
 * @type {string}
 */
const MODAL_LOG_NAME = 'Modal'


export default {

    data() {
        return {
            profile: null,
            error: null,
            message: null,
            msgType: 'default',
            showPasswordChanger: false,
            cookieConsent: null,
        }
    },

    mixins: [
        AnalyticsPage,
        InstanceTypeHelper,
        ResourcesHelper,
    ],

    computed: {
        ...Vuex.mapState([
            'authContext',
        ]),

        /**
         * @returns {MyAccountResources}
         */
        myResources() {
            return this.parseResources('views.accountSettings')
        },

        /**
         * @returns {boolean}
         */
        supportsEvaluationConsent() {
            return this.$scheme.showEvaluationConsent
        },

        /**
         * @returns {boolean}
         */
        supportsMarketingConsent() {
            return this.$scheme.showMarketingConsent
        },
    },

    async mounted() {
        try {
            await this._loadDataAsync()
        } catch (err) {
            this.error = `Unable to load user info: ${ err.message }`
            throw err
        }
    },

    methods: {
        /**
         * Returns the actionGranted text if state is true, otherwise actionWithdrawn
         * @param {boolean} state
         * @returns {string}
         */
        displayAcceptedOrWithdrawn(state) {
            return state ? this.myResources.actionAccepted : this.myResources.actionWithdrawn
        },

        /**
         * Reset error messages.
         */
        _clearMessages() {
            this.message = null
            this.error = null
        },

        /**
         * Load profile and cookie consent data.
         */
        async _loadDataAsync() {
            try {
                this.profile = await UserManager.loadProfileDataAsync()
                this.$logger.logObject({ profile: this.profile }, 'Loading addresses from profile', DEFAULT_LOG_NAME)
                if (this.profile) {
                    this.$logger.log(`Setting ${ this.profile.addresses.length } addresses`, DEFAULT_LOG_NAME)
                    PropertyManager.storeLinkedProperties(this.profile.addresses)
                }
            } catch (err) {
                this.error = `Unable to load user info: ${ err.message }`
                throw err
            }

            try {
                this.cookieConsent = await CookieConsentManager.getConsentDataAsync()
                this.$logger.logObject({ cookieConsent: this.cookieConsent }, 'Loading cookie consent data', DEFAULT_LOG_NAME)
            } catch (err) {
                this.error = `Unable to load cookie consent data: ${ err.message }`
                throw err
            }
        },

        /**
         * Triggers when clicking Change Password.
         */
        onChangePasswordClick() {
            this._clearMessages()
            this.showPasswordChanger = true
        },

        /**
         * Triggers when clearing validation.
         */
        onClearValidation() {
            this._clearMessages()
        },

        /**
         * Triggers when clicking Contact Made.
         * @async
         */
        async onContactMadeClick() {
            try {
                await UserManager.confirmContactAsync()
            } catch (err) {
                this.error = `Unable to confirm that contact has been made: ${ err.message }`
                throw err
            }
        },

        /**
         * Triggers when clicking Delete.
         */
        onDeleteClick() {
            this._clearMessages()
            this.$eventbus.$off(this.$appevents.afterModalClose)
            this.$eventbus.$on(this.$appevents.afterModalClose, () => {
                this.$nextTick(() => {
                    const button = this.$refs.deleteButton
                    this.$logger.logObject({ ref: button }, 'Post close action', MODAL_LOG_NAME)
                    button.$el.focus()
                })
            })
            this.$eventbus.$emit(this.$appevents.showModal, DeleteConfirmation)
        },

        /**
         * Triggers when clicking Log Out.
         */
        onLogOutClick() {
            this._clearMessages()
            this.$eventbus.$off(this.$appevents.afterModalClose)
            this.$eventbus.$on(this.$appevents.afterModalClose, () => {
                this.$nextTick(() => {
                    const button = this.$refs.logOutButton
                    this.$logger.logObject({ ref: button }, 'Post close action', MODAL_LOG_NAME)
                    button.$el.focus()
                })
            })
            history.pushState(null, 'Log out confirmation', '#')
            this.$eventbus.$emit(this.$appevents.showModal, LogOutConfirmation)
        },

        /**
         * Triggers when changing the password is changed.
         * @param {Object} result The result of changing.
         */
        onPasswordChanged(result) {
            if (result && result.error) {
                this.error = result.error
            } else if (result && result.message) {
                this.message = result.message
                this.msgType = 'ok'
                this.showPasswordChanger = false
            }
        },

        /**
         * Triggers when changing the password is cancelled.
         */
        onPasswordChangeCanceled() {
            this.showPasswordChanger = false
        },

        /**
         * Grants consent for a type of contact.
         * @async
         * @param {string} consentType The type of consent to change.
         */
        async onGrantConsent(consentType) {
            try {
                const result = await UserManager.consentToAsync(consentType)
                this.profile = result ? result : this.profile
            } catch (err) {
                this.error = `Unable to grant ${ consentType } consent: ${ err.message }`
                throw err
            }
        },

        /**
         * Withdraws consent for a type of contact.
         * @async
         * @param {string} consentType The type of consent to change.
         */
        async onWithdrawConsent(consentType) {
            try {
                const result = await UserManager.withdrawConsentForAsync(consentType)
                this.profile = result ? result : this.profile
            } catch (err) {
                this.error = `Unable to withdraw ${ consentType } consent: ${ err.message }`
                throw err
            }
        },

        /**
         * Withdraw the provided cookie consent type.
         * @async
         * @param {string} consentType The property on the cookie consent object to change.
         */
        async onWithdrawCookieConsent(consentType) {
            this.cookieConsent[consentType] = false

            await CookieConsentManager.setConsentDataAsync(this.cookieConsent)
        },

        /**
         * Grant the provided cookie consent type.
         * @async
         * @param {string} consentType The property on the cookie consent object to change.
         */
        async onGrantCookieConsent(consentType) {
            this.cookieConsent[consentType] = true

            await CookieConsentManager.setConsentDataAsync(this.cookieConsent)
        },
    },

    components: {
        ConsentManager,
        PasswordChanger,
        LoadingAnimation,
    },
}
</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_my-account.scss

$mobile-button-area-height: 105px;

.my-account {

    @include bp-min-mid {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
    }

    @include element('main') {
        flex: 3 1 auto;
        padding-bottom: $mobile-button-area-height;
    }

    @include element('subtitle') {
        margin-bottom: 5px;
    }

    @include element('data') {
        margin: 0 0 15px 0;

        @include element('item') {
            margin-bottom: 10px;

            @include bp-min-small {
                margin-bottom: 5px;
            }

            @include element('label') {
                font-weight: bold;

                @include bp-min-small {
                    display: inline;

                    &:after {
                        content: ':'
                    }
                }
            }

            @include element('value') {
                margin-inline-start: 0;

                @include bp-min-small {
                    display: inline;
                    margin-inline-start: 10px;
                }
            }
        }
    }

    @include element('operations') {
        margin: 1.2rem 0;
    }

    @include elements('addresses' 'renovations') {
        @include styled-data-list;
        margin-bottom: 15px;
    }

    @include element('buttons') {
        position: absolute;
        bottom: $footer-height-base;
        left: 0;
        width: 100%;
        padding: 10px;
        z-index: z-index('under-mask');
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        @include bp-min-mid {
            position: relative;
            z-index: 1;
            bottom: 0;
            padding: 0;
            flex: 1 0 auto;
            width: auto;
            max-width: 220px;
        }

        @include element('button') {
            width: 100%;
            min-width: 140px;
            margin-bottom: 1rem;

            @include modifier('change-password') {
                display: none;

                @include bp-min-mid {
                    display: block;
                }
            }
        }
    }
}

</style>
